import type {
  DashboardState, SubmitReviewBody,
  GetFileResponse, GetHeadResponse, CommitEditResponse,
  ArticlesResponse, GetArticleResponse, DirectPrBody, DirectPrResponse,
} from '@shared/types';
import { store } from '../store';
import { setToken } from '../store/slices/authSlice';

export class AuthError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = 'AuthError';
  }
}

async function call<T>(
  token: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      'content-type': 'application/json',
      'x-dashboard-token': token,
    },
  });
  if (res.status === 401) {
    store.dispatch(setToken(null));
    throw new AuthError('unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const snippet = text.length > 200 ? `${text.slice(0, 200)}…` : text;
    throw new Error(`${res.status}${snippet ? ` ${snippet}` : ''}`);
  }
  return (await res.json()) as T;
}

async function callRaw<T>(
  token: string,
  path: string,
  init: RequestInit,
): Promise<T> {
  // For multipart/form-data, do NOT set content-type — let the browser do it.
  const res = await fetch(path, {
    ...init,
    headers: { ...(init.headers ?? {}), 'x-dashboard-token': token },
  });
  if (res.status === 401) {
    store.dispatch(setToken(null));
    throw new AuthError('unauthorized');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const snippet = text.length > 200 ? `${text.slice(0, 200)}…` : text;
    throw new Error(`${res.status}${snippet ? ` ${snippet}` : ''}`);
  }
  return (await res.json()) as T;
}

export const api = {
  state: (token: string) => call<DashboardState>(token, '/api/state'),
  submitReview: (token: string, n: number, body: SubmitReviewBody) =>
    call<{ ok: true }>(token, `/api/pr/${n}/review`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  getHead: (token: string, n: number) =>
    call<GetHeadResponse>(token, `/api/pr/${n}/head`),
  getFile: (token: string, n: number, path: string) =>
    call<GetFileResponse>(
      token,
      `/api/pr/${n}/file?path=${encodeURIComponent(path)}`,
    ),
  commitEdit: (
    token: string,
    n: number,
    args: {
      filePath: string;
      mdxContent: string;
      expectedHeadSha: string;
      images: { filename: string; file: File }[];
    },
  ) => {
    const fd = new FormData();
    fd.append('filePath', args.filePath);
    fd.append('mdxContent', args.mdxContent);
    fd.append('expectedHeadSha', args.expectedHeadSha);
    for (const img of args.images) {
      fd.append(`image:${img.filename}`, img.file, img.filename);
    }
    return callRaw<CommitEditResponse>(token, `/api/pr/${n}/commit-edit`, {
      method: 'POST', body: fd,
    });
  },
  articles: (token: string) =>
    call<ArticlesResponse>(token, '/api/articles'),
  getArticle: (token: string, path: string) =>
    call<GetArticleResponse>(
      token,
      `/api/article?path=${encodeURIComponent(path)}`,
    ),
  directPr: (token: string, body: DirectPrBody) =>
    call<DirectPrResponse>(token, '/api/direct-pr', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  repoImage: async (token: string, path: string): Promise<string> => {
    const res = await fetch(`/api/repo-image?path=${encodeURIComponent(path)}`, {
      headers: { 'x-dashboard-token': token },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};
